/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './icd-details.reducer';

export const IcdDetails = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const icdDetailsList = useAppSelector(state => state.icdDetails.entities);
  const loading = useAppSelector(state => state.icdDetails.loading);
  const totalItems = useAppSelector(state => state.icdDetails.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="icd-details-heading" data-cy="IcdDetailsHeading">
        <Translate contentKey="tinyMedicRestApp.icdDetails.home.title">Icd Details</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.icdDetails.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/icd-details/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.icdDetails.home.createLabel">Create new Icd Details</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.icdDetails.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {icdDetailsList && icdDetailsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('icd9Code')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icd9Code">Icd 9 Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icd9Code')} />
                </th>
                <th className="hand" onClick={sort('icd10Code')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icd10Code">Icd 10 Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icd10Code')} />
                </th>
                <th className="hand" onClick={sort('icd9Descrip')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icd9Descrip">Icd 9 Descrip</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icd9Descrip')} />
                </th>
                <th className="hand" onClick={sort('icd10Descrip')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icd10Descrip">Icd 10 Descrip</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icd10Descrip')} />
                </th>
                <th className="hand" onClick={sort('icdmetadesc')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icdmetadesc">Icdmetadesc</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icdmetadesc')} />
                </th>
                <th className="hand" onClick={sort('icdng')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icdng">Icdng</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icdng')} />
                </th>
                <th className="hand" onClick={sort('icddrg')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icddrg">Icddrg</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icddrg')} />
                </th>
                <th className="hand" onClick={sort('icdnum')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icdnum">Icdnum</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icdnum')} />
                </th>
                <th className="hand" onClick={sort('icdamt')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icdamt">Icdamt</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icdamt')} />
                </th>
                <th className="hand" onClick={sort('icdcoll')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icdcoll">Icdcoll</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icdcoll')} />
                </th>
                <th className="hand" onClick={sort('icdarchive')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.icdarchive">Icdarchive</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('icdarchive')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.icdDetails.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {icdDetailsList.map((icdDetails, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/icd-details/${icdDetails.id}`} color="link" size="sm">
                      {icdDetails.id}
                    </Button>
                  </td>
                  <td>{icdDetails.icd9Code}</td>
                  <td>{icdDetails.icd10Code}</td>
                  <td>{icdDetails.icd9Descrip}</td>
                  <td>{icdDetails.icd10Descrip}</td>
                  <td>{icdDetails.icdmetadesc}</td>
                  <td>{icdDetails.icdng}</td>
                  <td>{icdDetails.icddrg}</td>
                  <td>{icdDetails.icdnum}</td>
                  <td>{icdDetails.icdamt}</td>
                  <td>{icdDetails.icdcoll}</td>
                  <td>{icdDetails.icdarchive}</td>
                  <td>{icdDetails.active ? 'true' : 'false'}</td>
                  <td>{icdDetails.timestampentry}</td>
                  <td>{icdDetails.microSec}</td>
                  <td>{icdDetails.lastupdated}</td>
                  <td>{icdDetails.cursor}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/icd-details/${icdDetails.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/icd-details/${icdDetails.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/icd-details/${icdDetails.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.icdDetails.home.notFound">No Icd Details found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={icdDetailsList && icdDetailsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default IcdDetails;
